import React, { useRef } from "react";
import Flex from "../../../../../components/Flex";
import { Button, Form, Spin, Typography } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { CoverWizardForm } from "../CoverWizardModal";

type WizardPageWrapperProps = {
  children: React.ReactNode;
  title: string;
  summary?: string | React.ReactNode;
  onNextButtonClick?: () => void;
  onBackButtonClick?: () => void;
  disablePrevious?: boolean;
  disableNext?: boolean;
  nextButtonText?: string;
  backButtonText?: string;
  submitOnNext?: boolean;
  nextIsLoading?: boolean;
  hideBackButton?: boolean;
  disableScroll?: boolean;
  style?: React.CSSProperties;
  pageCounter?: React.ReactNode;
  suppliedSummaryRef?: React.RefObject<HTMLDivElement>;
};

export default function WizardPageWrapper({
  children,
  title,
  summary,
  onNextButtonClick,
  onBackButtonClick,
  submitOnNext = false,
  nextButtonText = submitOnNext ? "Submit" : "Continue",
  backButtonText = "Back",
  style,
  disablePrevious = false,
  nextIsLoading = false,
  disableNext = false,
  hideBackButton = false,
  disableScroll = false,
  suppliedSummaryRef,
  pageCounter,
}: WizardPageWrapperProps) {
  const summaryRef = React.useRef<HTMLDivElement>(null);
  const titleRef = React.useRef<HTMLDivElement>(null);
  const footerRef = React.useRef<HTMLDivElement>(null);
  return (
    <Flex vertical style={style}>
      <Flex vertical style={{ width: "100%" }} className="fade-in">
        <Typography.Title ref={titleRef} level={1}>
          {title}
        </Typography.Title>
        {typeof summary === "string" ? (
          <Typography.Paragraph ref={summaryRef}>
            {summary}
          </Typography.Paragraph>
        ) : !!summary ? (
          summary
        ) : null}
        <div
          style={{
            marginTop: "10px",
            overflowY: disableScroll ? undefined : "auto",
            overflowX: "hidden",
            maxHeight: `calc(97vh - ${
              (titleRef.current?.clientHeight ?? 0) +
              (suppliedSummaryRef?.current?.clientHeight ??
                summaryRef.current?.clientHeight ??
                0) +
              (footerRef.current?.clientHeight ?? 0) +
              180
            }px)`, // Ensure minimum height for content
            flex: "1 1 auto", // Allow div to grow/shrink while maintaining scroll
          }}
        >
          {children}
        </div>
      </Flex>
      {(!!onNextButtonClick || !!onBackButtonClick) && (
        <Flex
          justify={hideBackButton ? "flex-end" : "space-between"}
          align="center"
          style={{ marginTop: "40px" }}
          ref={footerRef}
        >
          {!hideBackButton && (
            <Button
              style={{ height: "37px", minWidth: "100px", borderRadius: "8px" }}
              disabled={nextIsLoading || disablePrevious}
              onClick={onBackButtonClick}
            >
              {backButtonText}
            </Button>
          )}
          {pageCounter}
          <Button
            style={{ height: "37px", minWidth: "100px", borderRadius: "8px" }}
            type="primary"
            htmlType={submitOnNext ? "submit" : undefined}
            disabled={disableNext || nextIsLoading}
            onClick={onNextButtonClick}
          >
            <Flex align="center" justify="center" gap="4px">
              <span>{nextButtonText}</span>
              {nextIsLoading && (
                <Spin size="small" indicator={<LoadingOutlined spin />} />
              )}
            </Flex>
          </Button>
        </Flex>
      )}
    </Flex>
  );
}

export function GeneralPageSummary() {
  const form = Form.useFormInstance<CoverWizardForm>();
  // the second param is required otherwise the variable wont update
  const jobName =
    Form.useWatch("jobName", { form: form, preserve: true }) ?? "";
  const poNumber =
    Form.useWatch("poNumber", { form: form, preserve: true }) ?? "";
  const quoteType =
    Form.useWatch("quoteType", { form: form, preserve: true }) ?? "";
  return (
    <Typography.Text>
      {!!poNumber && !!jobName
        ? `PO: ${poNumber}\\${jobName}`
        : !!poNumber
        ? `PO: ${poNumber}`
        : jobName}{" "}
      - {quoteType}
    </Typography.Text>
  );
}
